import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Calendar,
  Plane,
  Download,
  FileText,
  Upload,
  Plus,
  Pencil,
  Trash2,
  Search,
  ChevronDown,
  RefreshCw,
} from "lucide-react";
import useSearchPagination from "../../hooks/useSearchPagination";
import { API_URL } from "../../api/api";
import { T } from "../../components/em/tokens";
import { Tabs } from "../../components/em/ui";

import { confirmDialog } from "../../components/em/confirm";
import { toast } from "../../utils/toast";
import { hasPermission } from "../../utils/permissionUtils";
import LeaveTypeFormModal from "./LeaveTypeFormModal";
import GreytHRSync from "./GreytHRSync";
const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  // .main-header is a sticky element pinned at z-index: 1000 — at 400 this
  // overlay (and the modal's own header/title inside it) rendered BELOW it,
  // invisibly painted over for the top ~100px band the nav bar occupies.
  zIndex: 1200,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};
const modalCard = {
  width: "min(460px,100%)",
  background: "var(--em-bg-card)",
  borderRadius: 14,
  overflow: "hidden",
  boxShadow: "0 20px 40px -12px rgba(0,0,0,0.25)",
};
const fieldLabel = {
  font: `500 13px ${T.font}`,
  color: T.fg1,
  marginBottom: 8,
  display: "block",
};
const fieldInput = {
  width: "100%",
  padding: "11px 14px",
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  font: `400 14px ${T.font}`,
  color: T.fg1,
  outline: "none",
  boxSizing: "border-box",
  background: "var(--em-bg-card)",
};
const headerBtn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 12px",
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  background: "var(--em-bg-card)",
  color: T.fg1,
  font: `500 12px ${T.font}`,
  cursor: "pointer",
};

const CalendarSettings = () => {
  const baseURL = `${API_URL}/api/settings/calendar`;
  const leavebaseURL = `${API_URL}/api`;
  const token = localStorage.getItem("token");

  // Holidays are `manage_calendar_settings`; leave types are
  // `manage_leave_types` — the keys the /settings/calendar endpoints enforce.
  const canHolidays = hasPermission("manage_calendar_settings");
  const canLeaveTypes = hasPermission("manage_leave_types");
  // GreytHR Sync tab — `manage_greythr_sync`, enforced by /api/greythr.
  const canGreythr = hasPermission("manage_greythr_sync");
  const [activeTab, setActiveTab] = useState(
    canHolidays
      ? "holidays"
      : canLeaveTypes
        ? "leavetypes"
        : canGreythr
          ? "greythr"
          : "holidays",
  );
  const [holidays, setHolidays] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [empTypeNames, setEmpTypeNames] = useState(new Map());
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  // Holidays list, scoped to one year — the table stores one row per
  // occurrence (a recurring holiday still has a single literal `date`, just
  // flagged "Recurring" for the calendar view's own expansion), so the years
  // on offer here are whatever years those rows actually fall in.
  const currentYear = new Date().getFullYear();
  const [yearFilter, setYearFilter] = useState(currentYear);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  // Holiday form only — leave types have their own <LeaveTypeFormModal>.
  const [formData, setFormData] = useState({
    name: "",
    date: "",
    type: "Public",
    color: "#ff0000",
    repeat_annually: false,
  });

  useEffect(() => {
    setCurrentPage(1);
    if (activeTab === "holidays") fetchHolidays();
    else if (activeTab === "leavetypes") fetchLeaveTypes();
    // The GreytHR Sync tab loads its own data.
    else if (activeTab !== "greythr") fetchLeaveRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchLeaveRequests = async () => {
    try {
      const res = await axios.get(`${leavebaseURL}/leaves/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeaveRequests(res.data || []);
    } catch (err) {
      console.error("Error fetching leave requests", err);
    }
  };
  const fetchHolidays = async () => {
    try {
      const res = await axios.get(`${baseURL}/holidays`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHolidays(res.data || []);
    } catch (err) {
      console.error("Error fetching holidays", err);
    }
  };
  const fetchLeaveTypes = async () => {
    try {
      const [res, typesRes] = await Promise.all([
        axios.get(`${baseURL}/leave-types`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        // For the "applies to" tags on each row.
        axios
          .get(`${API_URL}/api/settings/employment-types`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .catch(() => ({ data: [] })),
      ]);
      setLeaveTypes(res.data || []);
      setEmpTypeNames(
        new Map((typesRes.data || []).map((t) => [Number(t.id), t.name])),
      );
    } catch (err) {
      console.error("Error fetching leave types", err);
    }
  };

  // Employee-type names a leave type applies to; null = every type. Ids of
  // since-deleted employee types are dropped.
  const appliesToNames = (lt) => {
    let ids = null;
    try {
      const raw = lt.applies_to_types;
      ids = raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) : null;
    } catch {
      ids = null;
    }
    if (!Array.isArray(ids) || !ids.length) return null;
    return ids.map((id) => empTypeNames.get(Number(id))).filter(Boolean);
  };

  // Every year that has at least one holiday row, plus the current year so
  // the dropdown always has a sensible default even before anything has been
  // added for it yet.
  const holidayYears = useMemo(() => {
    const years = new Set([currentYear]);
    holidays.forEach((h) => {
      const y = h.date ? new Date(h.date).getFullYear() : NaN;
      if (!Number.isNaN(y)) years.add(y);
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [holidays, currentYear]);

  const yearFilteredHolidays = useMemo(
    () =>
      holidays.filter(
        (h) => h.date && new Date(h.date).getFullYear() === yearFilter,
      ),
    [holidays, yearFilter],
  );

  const sourceData =
    activeTab === "holidays"
      ? yearFilteredHolidays
      : activeTab === "leavetypes"
        ? leaveTypes
        : leaveRequests;
  const { filteredData, currentData, totalPages } = useSearchPagination(
    sourceData,
    searchTerm,
    currentPage,
  );

  const openModal = (item = null) => {
    setEditItem(item);
    if (activeTab === "leavetypes") {
      setShowModal(true);
      return;
    }
    if (item) {
      let formattedDate = "";
      if (item.date) {
        const dateObj = new Date(item.date);
        const local = new Date(
          dateObj.getTime() - dateObj.getTimezoneOffset() * 60000,
        );
        formattedDate = local.toISOString().split("T")[0];
      }
      setFormData({
        name: item.name || "",
        date: formattedDate || "",
        type: item.type || "Public",
        color: item.color || "#ff0000",
        repeat_annually: !!item.repeat_annually,
      });
    } else {
      setFormData({
        name: "",
        date: "",
        type: "Public",
        color: "#ff0000",
        repeat_annually: false,
      });
    }
    setShowModal(true);
  };

  // Holidays only; leave types save from <LeaveTypeFormModal>.
  const handleSave = async () => {
    const authH = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const payload = {
        name: formData.name,
        date: formData.date,
        type: formData.type,
        color: formData.color,
        repeat_annually: formData.repeat_annually,
      };
      if (!payload.name || !payload.date) {
        toast.error("Please enter holiday name and date");
        return;
      }
      if (editItem)
        await axios.put(`${baseURL}/holidays/${editItem.id}`, payload, authH);
      else await axios.post(`${baseURL}/holidays`, payload, authH);
      fetchHolidays();
      setShowModal(false);
      setEditItem(null);
    } catch (err) {
      console.error("Error saving item", err);
    }
  };

  const handleDelete = async (id) => {
    if (!(await confirmDialog("Are you sure?"))) return;
    const authH = { headers: { Authorization: `Bearer ${token}` } };
    try {
      if (activeTab === "holidays") {
        await axios.delete(`${baseURL}/holidays/${id}`, authH);
        fetchHolidays();
      } else {
        await axios.delete(`${baseURL}/leave-types/${id}`, authH);
        fetchLeaveTypes();
      }
    } catch (err) {
      console.error("Error deleting", err);
    }
  };

  const handleExportHolidays = async () => {
    try {
      const response = await axios.get(`${baseURL}/holidays/export`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "holidays.csv";
      link.click();
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const handleExportTemplate = async () => {
    try {
      const response = await axios.get(`${baseURL}/holidays/template`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([response.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "template.csv";
      link.click();
    } catch (err) {
      toast.error("Template export failed");
    }
  };

  const handleImportCSV = async () => {
    if (!csvFile) {
      toast.error("Upload a CSV file");
      return;
    }
    const fd = new FormData();
    fd.append("file", csvFile);
    try {
      const res = await axios.post(`${baseURL}/holidays/import`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { inserted = 0, skipped = 0, reasons = [] } = res.data || {};

      if (inserted) {
        toast.success(`Imported ${inserted} holiday${inserted === 1 ? "" : "s"}`);
      }
      if (skipped) {
        // Say *why* rows were dropped — a bare count leaves no way to tell a
        // duplicate from a malformed date.
        console.warn("Holiday import skipped rows:", reasons);
        toast.warning(
          `Skipped ${skipped}: ${reasons.slice(0, 2).join(" · ") || "invalid rows"}` +
            (reasons.length > 2 ? ` (+${reasons.length - 2} more)` : ""),
        );
      }
      if (!inserted && !skipped) {
        toast.warning("No rows found in that CSV");
      }

      setShowImportModal(false);
      fetchHolidays();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Import failed");
    }
  };

  // `icon` (lowercase) is what the shared <Tabs> reads.
  const TABS = [
    canHolidays && { key: "holidays", label: "Holidays & Festivals", icon: Calendar },
    canLeaveTypes && { key: "leavetypes", label: "Leave Types & Policies", icon: Plane },
    canGreythr && { key: "greythr", label: "GreytHR Sync", icon: RefreshCw },
  ].filter(Boolean);

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 18,
        }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            background: T.orange50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Calendar size={20} color={T.orange} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              font: `700 20px ${T.font}`,
              color: T.fg1,
              letterSpacing: "-0.02em",
            }}
          >
            Calendar Settings
          </div>
          <div style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>
            Manage holidays, leave types, and calendar policies
          </div>
        </div>
      </div>

      {/* Tabs — shared segmented control, same as Attendance. */}
      <div style={{ marginBottom: 18 }}>
        <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />
      </div>

      {/* Search + (holidays only) year filter. The GreytHR tab has its own. */}
      <div
        style={{
          display: activeTab === "greythr" ? "none" : "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <div style={{ position: "relative", maxWidth: 280, flex: "1 1 200px" }}>
          <Search
            size={13}
            color={T.fg7}
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
          <input
            type="text"
            placeholder={`Search ${activeTab === "holidays" ? "holidays" : "leave types"}...`}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              width: "100%",
              height: 36,
              padding: "0 12px 0 30px",
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              font: `400 13px ${T.font}`,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>
        {activeTab === "holidays" && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* A <select>'s text is centred by the browser's native widget
                rendering, not by flex/align-items on its box — so matching
                this label's box height to the select's was never going to
                line the two up. `line-height` equal to the content height
                (both sides) is what actually centres text in a native
                control; giving the label the same line-height puts its
                baseline on the same line as the select's. */}
            {/* The select's own dropdown arrow relies on the global
                `select { background: url(...) }` rule, which this box's own
                `background` (needed for theme support) would silently
                override — so the arrow is drawn as a separate icon instead,
                the same way the search icon above is. `height` and
                `boxSizing` match the search input exactly so the two controls
                sit on one line instead of the select riding on the global
                `select { height: 40px }` rule. */}
            <div style={{ position: "relative", display: "inline-flex" }}>
              <select
                id="cal-holiday-year"
                value={yearFilter}
                onChange={(e) => {
                  setYearFilter(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={{
                  height: 36,
                  lineHeight: "34px", // 36 minus the 1px top+bottom border
                  boxSizing: "border-box",
                  padding: "0 30px 0 12px",
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  font: `500 13px ${T.font}`,
                  color: T.fg1,
                  background: "var(--em-bg-card)",
                  outline: "none",
                  cursor: "pointer",
                }}
              >
                {holidayYears.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={14}
                color={T.fg7}
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                }}
              />
            </div>
          </div>
        )}
      </div>

      {activeTab === "greythr" && <GreytHRSync />}

      {/* HOLIDAYS */}
      {activeTab === "holidays" && (
        <div
          style={{
            background: "var(--em-bg-card)",
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            overflow: "hidden",
            boxShadow: T.shadowSm,
          }}
        >
          <div
            style={{
              padding: "16px 20px",
              borderBottom: `1px solid ${T.divider}`,
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>
                Company Holidays
              </div>
              <div style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>
                {yearFilteredHolidays.length} holidays configured for {yearFilter}
              </div>
            </div>
            <button onClick={handleExportHolidays} style={headerBtn}>
              <Download size={14} /> Export
            </button>
            <button onClick={handleExportTemplate} style={headerBtn}>
              <FileText size={14} /> Template
            </button>
            <button onClick={() => setShowImportModal(true)} style={headerBtn}>
              <Upload size={14} /> Bulk import
            </button>
            <button
              onClick={() => openModal()}
              style={{
                ...headerBtn,
                border: 0,
                background: T.gradient,
                color: "#fff",
                font: `700 12px ${T.font}`,
                boxShadow: T.shadowBrand,
              }}
            >
              <Plus size={14} /> Add Holiday
            </button>
          </div>
          {currentData.length > 0 ? (
            currentData.map((h, i) => (
              <div
                key={h.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 20px",
                  borderBottom:
                    i < currentData.length - 1
                      ? `1px solid ${T.divider}`
                      : "none",
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 9999,
                    background: h.color,
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <b style={{ font: `600 14px ${T.font}`, color: T.fg1 }}>
                      {h.name}
                    </b>
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: 9999,
                        background: h.repeat_annually ? T.success50 : T.bgMuted,
                        color: h.repeat_annually ? T.success600 : T.fg5,
                        font: `600 10px ${T.font}`,
                      }}
                    >
                      {h.repeat_annually ? "Recurring" : "Not Recurring"}
                    </span>
                  </div>
                  <div
                    style={{
                      font: `400 12px ${T.font}`,
                      color: T.fg5,
                      marginTop: 2,
                    }}
                  >
                    {new Date(h.date).toLocaleDateString()}
                  </div>
                </div>
                <span
                  style={{
                    padding: "7px 10px",
                    borderRadius: 9999,
                    background: T.orangeSoft,
                    color: T.orange,
                    font: `600 11px ${T.font}`,
                  }}
                >
                  {h.type}
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => openModal(h)}
                    title="Edit"
                    style={{
                      padding: 0,
                      width: 30,
                      height: 30,
                      borderRadius: 7,
                      border: `1px solid ${T.border}`,
                      background: "transparent",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: T.fg4,
                    }}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => handleDelete(h.id)}
                    title="Delete"
                    style={{
                      padding: 0,
                      width: 30,
                      height: 30,
                      borderRadius: 7,
                      border: "1px solid #fecaca",
                      background: T.danger50,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: T.danger,
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                font: `400 13px ${T.font}`,
                color: T.fg7,
              }}
            >
              No holidays found
            </div>
          )}
        </div>
      )}

      {/* LEAVE TYPES */}
      {activeTab === "leavetypes" && (
        <div
          style={{
            background: "var(--em-bg-card)",
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            overflow: "hidden",
            boxShadow: T.shadowSm,
          }}
        >
          <div
            style={{
              padding: "16px 20px",
              borderBottom: `1px solid ${T.divider}`,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>
                Leave Types
              </div>
              <div style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>
                {leaveTypes.length} leave types configured
              </div>
            </div>
            <button
              onClick={() => openModal()}
              style={{
                ...headerBtn,
                border: 0,
                background: T.gradient,
                color: "#fff",
                font: `700 12px ${T.font}`,
                boxShadow: T.shadowBrand,
              }}
            >
              <Plus size={14} /> Add Leave Type
            </button>
          </div>
          {currentData.length > 0 ? (
            currentData.map((lt, i) => (
              <div
                key={lt.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  padding: "14px 20px",
                  borderBottom:
                    i < currentData.length - 1
                      ? `1px solid ${T.divider}`
                      : "none",
                }}
              >
                <span
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 9999,
                    background: lt.color,
                    flexShrink: 0,
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ font: `600 14px ${T.font}`, color: T.fg1 }}>
                    {lt.name}
                  </b>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 6,
                      marginTop: 4,
                    }}
                  >
                    <span style={{ font: `400 12px ${T.font}`, color: T.success600 }}>
                      Active
                    </span>
                    {/* Which employee types this applies to — tells apart
                        leave types that share a name. */}
                    {(appliesToNames(lt) || ["All employee types"]).map((n) => (
                      <span
                        key={n}
                        style={{
                          padding: "2px 8px",
                          borderRadius: 9999,
                          background: T.bgMuted,
                          border: `1px solid ${T.border}`,
                          color: T.fg3,
                          font: `500 11px ${T.font}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {n}
                      </span>
                    ))}
                  </div>
                </div>
                <span style={{ font: `400 12px ${T.font}`, color: T.fg5 }}>
                  Max Days:{" "}
                  <strong style={{ color: T.fg1 }}>
                    {Number(lt.max_days_per_year) || 0}
                  </strong>
                </span>
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: 9999,
                    background: lt.is_paid === 0 ? "rgba(234,179,8,0.14)" : T.success50,
                    color: lt.is_paid === 0 ? "#b45309" : T.success600,
                    font: `600 11px ${T.font}`,
                  }}
                >
                  {lt.is_paid === 0 ? "Unpaid" : "Paid"}
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    onClick={() => openModal(lt)}
                    title="Edit"
                    style={{
                      padding: 0,
                      width: 30,
                      height: 30,
                      borderRadius: 7,
                      border: `1px solid ${T.border}`,
                      background: "transparent",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: T.fg4,
                    }}
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => handleDelete(lt.id)}
                    title="Delete"
                    style={{
                      padding: 0,
                      width: 30,
                      height: 30,
                      borderRadius: 7,
                      border: "1px solid #fecaca",
                      background: T.danger50,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: T.danger,
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                font: `400 13px ${T.font}`,
                color: T.fg7,
              }}
            >
              No leave types found
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && activeTab !== "greythr" && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 8,
            marginTop: 16,
          }}
        >
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                border: `1px solid ${currentPage === i + 1 ? T.orange : T.border}`,
                background: currentPage === i + 1 ? T.bgBlack : "#fff",
                color: currentPage === i + 1 ? "#fff" : T.fg1,
                cursor: "pointer",
                padding: "0",
                font: `${currentPage === i + 1 ? 700 : 500} 13px ${T.font}`,
              }}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Import modal */}
      {showImportModal && (
        <div style={overlay} onClick={() => setShowImportModal(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                padding: "18px 22px",
                borderBottom: `1px solid ${T.divider}`,
              }}
            >
              <div style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>
                Import Holidays (CSV)
              </div>
            </div>
            <div style={{ padding: "20px 22px" }}>
              <label style={fieldLabel}>Select CSV File</label>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files[0])}
                style={{ font: `400 13px ${T.font}` }}
              />
              <div
                style={{
                  font: `400 12px ${T.font}`,
                  color: T.fg5,
                  marginTop: 12,
                }}
              >
                Required columns:{" "}
                <b>name, date, type, color, repeat_annually</b>
              </div>
            </div>
            <div
              style={{
                padding: "12px 22px",
                borderTop: `1px solid ${T.divider}`,
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowImportModal(false)}
                style={{
                  padding: "9px 16px",
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  cursor: "pointer",
                  background: "var(--em-bg-card)",
                  font: `600 14px ${T.font}`,
                  color: T.fg1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleImportCSV}
                style={{
                  padding: "9px 20px",
                  borderRadius: 8,
                  border: 0,
                  cursor: "pointer",
                  background: T.gradient,
                  color: "#fff",
                  font: `600 14px ${T.font}`,
                  boxShadow: T.shadowBrand,
                }}
              >
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit leave type */}
      {showModal && activeTab === "leavetypes" && (
        <LeaveTypeFormModal
          item={editItem}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            setEditItem(null);
            fetchLeaveTypes();
          }}
        />
      )}

      {/* Add/Edit holiday */}
      {showModal && activeTab === "holidays" && (
        <div style={overlay} onClick={() => setShowModal(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div
              style={{
                padding: "18px 22px",
                borderBottom: `1px solid ${T.divider}`,
              }}
            >
              <div style={{ font: `700 16px ${T.font}`, color: T.fg1 }}>
                {editItem ? "Edit Holiday" : "Add Holiday"}
              </div>
            </div>
            <div style={{ padding: "20px 22px" }}>
              <div style={{ marginBottom: 16 }}>
                <label style={fieldLabel}>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  style={fieldInput}
                />
              </div>
              {activeTab === "holidays" && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <label style={fieldLabel}>Date</label>
                    <input
                      type="date"
                      value={formData.date}
                      onChange={(e) =>
                        setFormData({ ...formData, date: e.target.value })
                      }
                      style={fieldInput}
                    />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={fieldLabel}>Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) =>
                        setFormData({ ...formData, type: e.target.value })
                      }
                      style={fieldInput}
                    >
                      <option value="Public">Public</option>
                      <option value="Optional">Optional</option>
                    </select>
                  </div>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      font: `500 13px ${T.font}`,
                      color: T.fg1,
                      marginBottom: 16,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={formData.repeat_annually}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          repeat_annually: e.target.checked,
                        })
                      }
                      style={{ accentColor: T.orange }}
                    />
                    Repeat Annually
                  </label>
                </>
              )}
            </div>
            <div
              style={{
                padding: "12px 22px",
                borderTop: `1px solid ${T.divider}`,
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: "9px 16px",
                  borderRadius: 8,
                  border: `1px solid ${T.border}`,
                  cursor: "pointer",
                  background: "var(--em-bg-card)",
                  font: `600 14px ${T.font}`,
                  color: T.fg1,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: "9px 20px",
                  borderRadius: 8,
                  border: 0,
                  cursor: "pointer",
                  background: T.gradient,
                  color: "#fff",
                  font: `600 14px ${T.font}`,
                  boxShadow: T.shadowBrand,
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarSettings;
